import { DesignerProductProvider } from 'src/modules/homeownerApp/constants/productProviders/configuration';
import type { ApiEnvironment } from 'src/shared/utils/environment';

import type { MaterialRegionOrPaint } from '../customizationCategories';

export enum DesignerPartner {
  YES_COLOURS = 'yes_colours',
  PELLA = 'pella',
  REALTOR = 'realtor',
  HARDIE = 'hardie',
  NONE = 'none',
}

type PartnerOrgIds = { [OrgId: string]: DesignerPartner };

interface BasePartnerMetadata {
  partner: DesignerPartner;
  configuration: DesignerPartnerConfiguration;
  orgId?: number;
  displayName?: string;
  logoUrl?: string;
  logoUrlInvert?: string;
}

export interface AssociatedPartnerMetadata extends BasePartnerMetadata {
  partner: Exclude<DesignerPartner, DesignerPartner.NONE>;
}

export interface NoPartnerMetadata extends BasePartnerMetadata {
  partner: DesignerPartner.NONE;
}

/**
 * Metadata for a partner association that includes both partner information
 * that comes from the backend (name, logo) and the hard-coded configuration.
 */
export type PartnerMetadata = AssociatedPartnerMetadata | NoPartnerMetadata;

/**
 * Hard-coded mapping of partner org IDs in our different API environments. The
 * org ID is mapped to the `DesignerPartner` enum, which can then be used to
 * retrieve the hard-coded partner configuration from the different mappings for
 * product catalog products, the lead form, etc.
 *
 * @remarks This is a stop-gap while we define how partner configuration will
 * actually work and move most or all the hard-coded configuration to the
 * backend.
 */
export const DESIGNER_PARTNER_MAP: Record<
  ApiEnvironment,
  PartnerOrgIds | null
> = {
  production: {
    '655657': DesignerPartner.YES_COLOURS,
    '752013': DesignerPartner.PELLA, // MEOW-5511
    '651112': DesignerPartner.REALTOR,
    '488993': DesignerPartner.HARDIE,
  },
  staging: {
    '32046': DesignerPartner.YES_COLOURS,
    '32047': DesignerPartner.PELLA, // MEOW-5511
    '38642': DesignerPartner.REALTOR,
    '13705': DesignerPartner.HARDIE,
  },
  sandbox: null,
  mock: null,
} as const;

export const HOMEOWNER_ORG_MAP: Record<ApiEnvironment, number> = {
  production: 23,
  staging: 34,
  sandbox: 32,
  mock: 23,
};

export interface DesignerPartnerConfiguration {
  brandTypes?: MaterialRegionOrPaint[];
  defaultLeadFormConfiguration: number;
  /**
   * Associate a Partner with a single Product Provider. This should only be
   * specified if the Partner is the same company as the Product Provider.
   */
  productProvider?: DesignerProductProvider;
  additionalLinkouts: string[];
  /**
   * The page to navigate to after a new project is created.
   */
  postProjectUploadNavigation?: 'customize' | 'project';
  /**
   * When true, manufacturer product links can be displayed
   * When undefined or false, manufacturer product links cannot be displayed
   */
  showManufacturerCTA?: boolean;
  nonPartnerPalettes?: string[];
}

/**
 * The primary hard-coded configuration for each partner. This has a 1-to-1
 * mapping with the partner org.
 */
export const PARTNER_CONFIGURATION_MAP: Record<
  DesignerPartner,
  DesignerPartnerConfiguration
> = {
  [DesignerPartner.PELLA]: {
    brandTypes: ['windows'],
    defaultLeadFormConfiguration: 3,
    postProjectUploadNavigation: 'customize',
    productProvider: DesignerProductProvider.PELLA,
    additionalLinkouts: [
      'pella_demystifying_windows',
      'pella_home_consultation',
      'pella_find_retailer',
      'pella_design_trends',
    ],
    showManufacturerCTA: true,
  },
  [DesignerPartner.REALTOR]: {
    defaultLeadFormConfiguration: 2,
    additionalLinkouts: [],
    showManufacturerCTA: true,
  },
  [DesignerPartner.HARDIE]: {
    brandTypes: ['walls', 'trim'],
    defaultLeadFormConfiguration: 2,
    productProvider: DesignerProductProvider.HARDIE,
    additionalLinkouts: [
      'james_hardie_get_quote',
      'james_hardie_request_sample',
      'james_hardie_choose_contractor',
      'james_hardie_see_how_protects',
    ],
    showManufacturerCTA: true,
    nonPartnerPalettes: ['Stone', 'Stucco', 'Brick'],
  },
  [DesignerPartner.NONE]: {
    defaultLeadFormConfiguration: 1,
    additionalLinkouts: [],
    /**
     * showManufacturerCTA is configured by default to be false to ensure
     * projects with pro-partners do not show manufacturer CTA's. A specific
     * exception to this configuration for projects with no partner is made in
     * useShowManufacturerCTA
     */
    showManufacturerCTA: false,
  },
  [DesignerPartner.YES_COLOURS]: {
    brandTypes: ['paint'],
    defaultLeadFormConfiguration: 3,
    additionalLinkouts: [],
  },
};

export const NO_PARTNER_METADATA: NoPartnerMetadata = {
  partner: DesignerPartner.NONE,
  configuration: PARTNER_CONFIGURATION_MAP[DesignerPartner.NONE],
};
